import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import {
  Building2, Users, Globe, TrendingUp, Shield, Clock,
  CheckCircle, ArrowRight, Sparkles, BarChart3, Zap, Heart
} from 'lucide-react';

const WhyJoinWayo = () => {
  const [formData, setFormData] = useState({
    institution_name: '',
    institution_type: '',
    location_city: '',
    location_state: '',
    location_country: 'United States',
    website: '',
    contact_name: '',
    contact_title: '',
    contact_email: '',
    contact_phone: '',
    enrollment_size: '',
    acceptance_rate: '',
    application_fee: '',
    why_join: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const benefits = [
    {
      icon: Users,
      title: "Access Diverse Talent",
      description: "Connect with over 1 million students from 150+ countries actively seeking higher education opportunities."
    },
    {
      icon: Zap,
      title: "Streamlined Applications",
      description: "Receive organized, complete applications through our standardized platform, reducing administrative burden."
    },
    {
      icon: BarChart3,
      title: "Data-Driven Insights",
      description: "Access analytics on applicant demographics, trends, and yield predictions to optimize your recruitment."
    },
    {
      icon: Globe,
      title: "Global Reach",
      description: "Expand your international student recruitment with our worldwide network and multilingual support."
    },
    {
      icon: Shield,
      title: "Verified Applications",
      description: "All applications include verified transcripts, test scores, and counselor recommendations."
    },
    {
      icon: Clock,
      title: "Real-Time Communication",
      description: "Directly communicate with applicants, send updates, and manage decisions through one platform."
    }
  ];

  const stats = [
    { value: "Growing", label: "Student Community" },
    { value: "Expanding", label: "College Network" },
    { value: "Global", label: "International Reach" },
    { value: "Free", label: "AI-Powered Tools" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Clean numeric fields - send null instead of empty strings
      const cleanData = {
        ...formData,
        enrollment_size: formData.enrollment_size ? parseInt(formData.enrollment_size) : null,
        acceptance_rate: formData.acceptance_rate ? parseFloat(formData.acceptance_rate) : null,
        application_fee: formData.application_fee ? parseInt(formData.application_fee) : null,
      };
      await api.post('/university-hub/membership/apply', cleanData);
      setSubmitted(true);
      toast({
        title: "Application Submitted!",
        description: "Our team will review your application and get back to you within 20-45 business days. Due to overwhelming response and extra workflow, there may be a delayed response. We appreciate your patience."
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to submit application. Please check all required fields.';
      toast({
        title: "Error",
        description: msg,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
              <Building2 className="w-5 h-5" />
              <span className="text-sm font-medium">For Colleges & Universities</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Partner with Wayo</h1>
            <p className="text-lg sm:text-xl opacity-90 mb-8">
              Join our growing network of institutions using Wayo to connect with motivated international students. 
              Streamline your admissions process and find your perfect candidates.
            </p>
            <a href="#apply-form">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100">
                Become a Member
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-6xl mx-auto px-4 -mt-10">
        <Card className="shadow-xl">
          <CardContent className="p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-emerald-600">{stat.value}</div>
                  <div className="text-gray-600 text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Benefits Section */}
      <div className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Why Institutions Choose Wayo</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We provide the tools and reach you need to build a diverse, talented incoming class.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">How Membership Works</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Apply", desc: "Submit your institution details" },
              { step: "2", title: "Verification", desc: "Our team verifies your institution" },
              { step: "3", title: "Setup", desc: "Configure your institution profile" },
              { step: "4", title: "Receive Applications", desc: "Start receiving student applications" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Application Form */}
      <div id="apply-form" className="max-w-4xl mx-auto px-4 py-20">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-emerald-600" />
              Membership Application
            </CardTitle>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Application Submitted!</h3>
                <p className="text-gray-600 mb-6">
                  Thank you for your interest in joining Wayo. Our team will review your application 
                  and contact you at {formData.contact_email} within 20-45 business days. Due to overwhelming response and extra workflow, there may be a delayed response. We appreciate your patience.
                </p>
                <Link to="/">
                  <Button>Return to Home</Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Institution Name *</Label>
                    <Input
                      required
                      value={formData.institution_name}
                      onChange={(e) => setFormData({...formData, institution_name: e.target.value})}
                      placeholder="University of Example"
                    />
                  </div>
                  <div>
                    <Label>Institution Type *</Label>
                    <Select
                      value={formData.institution_type}
                      onValueChange={(v) => setFormData({...formData, institution_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public_university">Public University</SelectItem>
                        <SelectItem value="private_university">Private University</SelectItem>
                        <SelectItem value="liberal_arts">Liberal Arts College</SelectItem>
                        <SelectItem value="community_college">Community College</SelectItem>
                        <SelectItem value="technical">Technical/Vocational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <Label>City *</Label>
                    <Input
                      required
                      value={formData.location_city}
                      onChange={(e) => setFormData({...formData, location_city: e.target.value})}
                      placeholder="Boston"
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <Input
                      required
                      value={formData.location_state}
                      onChange={(e) => setFormData({...formData, location_state: e.target.value})}
                      placeholder="Massachusetts"
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      placeholder="https://www.example.edu"
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h4 className="font-semibold mb-4">Primary Contact Information</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Contact Name *</Label>
                      <Input
                        required
                        value={formData.contact_name}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={formData.contact_title}
                        onChange={(e) => setFormData({...formData, contact_title: e.target.value})}
                        placeholder="Director of Admissions"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        required
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                        placeholder="admissions@example.edu"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Why do you want to join Wayo?</Label>
                  <Textarea
                    value={formData.why_join}
                    onChange={(e) => setFormData({...formData, why_join: e.target.value})}
                    placeholder="Tell us about your recruitment goals and how Wayo can help..."
                    rows={4}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Submitting..." : "Submit Application"}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you agree to our Terms of Service and Privacy Policy. 
                  Your application will be reviewed by our membership team.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WhyJoinWayo;
